import type Phaser from "phaser";
import { CANVAS_COLOURS, type BandRect, type CameraStopBand, type CameraStopLines } from "daedalus-core";

/**
 * The camera-stop guide (P1.6-T47): the band `cameraStopBand` gives, drawn over the map as a tint on each of
 * its sides with a dashed line along its inner edge, where the game's camera stops following the player.
 * One `Graphics` object, redrawn only when what it shows changes (the toggle, the band, the zoom or the part
 * of the inner edge in view), never every frame. The dashes are laid from each line's start, so a corner
 * opens on a dash and a pan never makes them crawl, and only those in view are drawn, so a large map at a
 * close zoom draws a screenful.
 */

/**
 * The guide's colour, `overlay-camera-stop` from the canvas colour table.
 */
export const CAMERA_STOP_COLOUR = CANVAS_COLOURS.cameraStop.rgb;

/**
 * The band's tint: the colour's own alpha drawn at 25%, a light mist the map still reads through.
 */
export const CAMERA_STOP_FILL_ALPHA = CANVAS_COLOURS.cameraStop.alpha * 0.25;

/**
 * A dash of the inner edge, in screen pixels at any zoom.
 */
export const CAMERA_STOP_DASH = 8;

/**
 * The gap after each dash, in screen pixels at any zoom.
 */
export const CAMERA_STOP_GAP = 6;

/**
 * The inner edge's line width, in screen pixels at any zoom.
 */
export const CAMERA_STOP_LINE = 2;

/**
 * The visible world rectangle and zoom the guide draws for.
 */
export interface CameraStopViewport {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly zoom: number;
}

/**
 * The dashes of a line along one axis that fall inside a range, laid from the line's start so they stay put
 * as the range moves: each dash starts a multiple of `dash + gap` after `from` and runs `dash`, clipped to the
 * line and the range.
 *
 * @param from The line's start.
 * @param to The line's end, at or after its start.
 * @param visibleFrom Where the range in view starts.
 * @param visibleTo Where it ends.
 * @param dash A dash's length.
 * @param gap The gap after each dash.
 * @returns Each visible dash as its start and end, in order.
 * @throws RangeError when the dash or the gap is not positive.
 * @example dashesAlong(0, 20, 0, 100, 8, 6) returns [[0, 8], [14, 20]].
 */
export function dashesAlong(from: number, to: number, visibleFrom: number, visibleTo: number, dash: number, gap: number): [number, number][] {
  if (!(dash > 0) || !(gap > 0)) {
    throw new RangeError(`dash ${dash} and gap ${gap} must be positive`);
  }
  const start = Math.max(from, visibleFrom);
  const end = Math.min(to, visibleTo);
  const period = dash + gap;
  const dashes: [number, number][] = [];
  for (let at = from + Math.floor((start - from) / period) * period; at < end; at += period) {
    const a = Math.max(at, start);
    const b = Math.min(at + dash, end);
    if (b > a) {
      dashes.push([a, b]);
    }
  }
  return dashes;
}

/**
 * The guide of one scene.
 */
export class CameraStopView {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private last = "";
  private draws = 0;

  /**
   * @param scene Scene to draw into.
   * @param depth Depth over the map's layers and under the editor's markers and overlays.
   */
  public constructor(scene: Phaser.Scene, depth: number) {
    this.graphics = scene.add.graphics().setDepth(depth);
  }

  /**
   * How many times the guide was drawn, which only a change of what it shows does.
   *
   * @returns The count.
   */
  public get redraws(): number {
    return this.draws;
  }

  /**
   * Draws the band when shown, or clears it, when anything it shows changed since the last call.
   *
   * @param band The band, or null when the guide is off or the game registers no camera.
   * @param view The visible world rectangle and zoom.
   * @returns True when a redraw happened.
   */
  public sync(band: CameraStopBand | null, view: CameraStopViewport): boolean {
    const signature = band === null ? "off" : `${CameraStopView.rectKey(band.lines.bounds)}:${band.lines.x.join(",")}:${band.lines.y.join(",")}|${CameraStopView.rectKey(band.inner)}|${CameraStopView.rectKey(band.sides.top)}|${CameraStopView.rectKey(band.sides.left)}|${CameraStopView.rectKey(band.sides.bottom)}|${CameraStopView.rectKey(band.sides.right)}|${band.lines.x.length + band.lines.y.length === 0 ? "" : `${Math.floor(view.left)},${Math.floor(view.top)},${Math.ceil(view.width)},${Math.ceil(view.height)},${view.zoom}`}`;
    if (signature === this.last) {
      return false;
    }
    this.last = signature;
    this.draws += 1;
    const g = this.graphics;
    g.clear();
    if (band === null) {
      return true;
    }
    g.fillStyle(CAMERA_STOP_COLOUR, CAMERA_STOP_FILL_ALPHA);
    for (const side of [band.sides.top, band.sides.right, band.sides.bottom, band.sides.left]) {
      if (side.width > 0 && side.height > 0) {
        g.fillRect(side.x, side.y, side.width, side.height);
      }
    }
    this.dashInnerEdge(band.lines, view);
    return true;
  }

  /**
   * Removes the guide from the scene.
   */
  public destroy(): void {
    this.graphics.destroy();
  }

  /**
   * Draws the inner edge's lines dashed, only where they are in view: each horizontal line across the lines'
   * bounds and each vertical line down them.
   *
   * @param lines The inner edge's lines.
   * @param view The visible world rectangle and zoom.
   */
  private dashInnerEdge(lines: CameraStopLines, view: CameraStopViewport): void {
    const g = this.graphics;
    const dash = CAMERA_STOP_DASH / view.zoom;
    const gap = CAMERA_STOP_GAP / view.zoom;
    const bounds = lines.bounds;
    const right = bounds.x + bounds.width;
    const bottom = bounds.y + bounds.height;
    const viewRight = view.left + view.width;
    const viewBottom = view.top + view.height;
    g.lineStyle(CAMERA_STOP_LINE / view.zoom, CAMERA_STOP_COLOUR, CANVAS_COLOURS.cameraStop.alpha);
    for (const y of lines.y) {
      if (y >= view.top && y <= viewBottom) {
        for (const [a, b] of dashesAlong(bounds.x, right, view.left, viewRight, dash, gap)) {
          g.lineBetween(a, y, b, y);
        }
      }
    }
    for (const x of lines.x) {
      if (x >= view.left && x <= viewRight) {
        for (const [a, b] of dashesAlong(bounds.y, bottom, view.top, viewBottom, dash, gap)) {
          g.lineBetween(x, a, x, b);
        }
      }
    }
  }

  /**
   * The part of the redraw signature a rectangle contributes.
   *
   * @param rect The rectangle, or null.
   * @returns A short stable string.
   */
  private static rectKey(rect: BandRect | null): string {
    return rect === null ? "-" : `${rect.x}:${rect.y}:${rect.width}:${rect.height}`;
  }
}
